import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarPlus, House, Palmtree, Thermometer } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  CountBadge,
  ErrorState,
  Meter,
  PersonCell,
  SearchField,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../../components/ui';
import { TimeOffType } from '../../store/slices/timeOffSlice';
import type { Employee } from '../../store/slices/employeeSlice';
import type { Department } from '../../store/slices/departmentSlice';
import { focusRing, sbg, typeStyle } from '../../theme/sbgTokens';
import { formatCount } from '../../utils/format';
import { MissingValue, NO_DEPARTMENT_LABEL, formatHours, possessive, useSlashToFocus } from './shared';

interface LogTimeOffPanelProps {
  departments: Department[];
  /** The current page of filtered employees, grouped by department id. */
  employeesByDepartment: Record<string, Employee[]>;
  totalEmployees: number;
  filteredCount: number;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  departmentFilter: string;
  onDepartmentChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onQuickLog: (employee: Employee, type: TimeOffType) => void;
  /** `${employeeId}:${type}` while a quick log is saving. */
  pendingQuickLog: string | null;
  onSchedule: (employee: Employee) => void;
}

const COLUMN_COUNT = 5;

const QUICK_LOGS: { type: TimeOffType; label: string; Icon: React.ElementType }[] = [
  { type: TimeOffType.PTO, label: 'PTO', Icon: Palmtree },
  { type: TimeOffType.SICK, label: 'a sick day', Icon: Thermometer },
  { type: TimeOffType.WFH, label: 'WFH', Icon: House },
];

const BalanceCell: React.FC<{ label: string; used: number; allotment: number; name: string }> = ({ label, used, allotment, name }) => {
  if (!allotment) return <MissingValue />;
  const left = allotment - used;
  const state = left < 0 ? `${formatHours(Math.abs(left))} over` : `${formatHours(left)} left`;
  return (
    <Meter
      // The bar stops at the allotment; the label carries the overage.
      value={Math.min(used, allotment)}
      max={allotment}
      tone={used > allotment ? 'danger' : 'brand'}
      label={`${formatHours(used)} of ${formatHours(allotment)}`}
      valueLabel={state}
      aria-label={`${possessive(name)} ${label}: ${formatHours(used)} used of ${formatHours(allotment)}, ${state}`}
      sx={{ minWidth: 160 }}
    />
  );
};

/** Hairline between the one-click logs and Schedule. */
const ActionDivider: React.FC = () => (
  <Box component="span" aria-hidden="true" sx={{ width: '1px', height: 16, mx: 0.5, bgcolor: sbg.border.default }} />
);

const LogTimeOffPanel: React.FC<LogTimeOffPanelProps> = ({
  departments,
  employeesByDepartment,
  totalEmployees,
  filteredCount,
  searchTerm,
  onSearchChange,
  departmentFilter,
  onDepartmentChange,
  page,
  rowsPerPage,
  onPageChange,
  loading,
  error,
  onRetry,
  onQuickLog,
  pendingQuickLog,
  onSchedule,
}) => {
  const searchRef = React.useRef<HTMLInputElement>(null);
  useSlashToFocus(searchRef);

  const departmentName = (deptId: string, members: Employee[]) =>
    departments.find((dept) => dept._id === deptId)?.name || members[0]?.department?.name || NO_DEPARTMENT_LABEL;
  const selectedDepartmentName = departmentFilter ? departments.find((dept) => dept._id === departmentFilter)?.name : undefined;
  const filtersActive = Boolean(searchTerm || departmentFilter);
  const groups = Object.entries(employeesByDepartment);

  const clearFilters = () => {
    onSearchChange('');
    onDepartmentChange('');
  };

  const toolbar = (
    <Toolbar
      end={
        !loading && !error ? (
          <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }} aria-live="polite">
            {filtersActive ? `${filteredCount} of ${formatCount(totalEmployees, 'employee')}` : formatCount(totalEmployees, 'employee')}
          </Typography>
        ) : undefined
      }
    >
      <SearchField value={searchTerm} onChange={onSearchChange} label="Search employees" placeholder="Search employees by name" inputRef={searchRef} autoFocus />
      <Select
        size="small"
        value={departmentFilter}
        onChange={(event) => onDepartmentChange(event.target.value)}
        displayEmpty
        inputProps={{ 'aria-label': 'Filter by department' }}
        renderValue={(value) => (value ? selectedDepartmentName ?? 'Department' : 'All departments')}
        sx={{ minWidth: 184, maxWidth: { xs: '100%', sm: 260 } }}
      >
        <MenuItem value="">All departments</MenuItem>
        {departments.map((dept) => (
          <MenuItem key={dept._id} value={dept._id}>
            {dept.name}
          </MenuItem>
        ))}
      </Select>
    </Toolbar>
  );

  // One <tbody> per department so each department header labels its own row group.
  let bodies: React.ReactNode;
  const singleBody = (content: React.ReactNode) => <TableBody>{content}</TableBody>;
  if (error) {
    bodies = singleBody(
      <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
        <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 0 }}>
          <ErrorState compact title="Couldn’t load employees" body="The server didn’t respond. Try again in a minute; your search is kept." onRetry={onRetry} />
        </TableCell>
      </TableRow>
    );
  } else if (loading) {
    bodies = singleBody(<TableSkeletonRows columns={COLUMN_COUNT} />);
  } else if (totalEmployees === 0) {
    bodies = singleBody(
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No employees yet"
        body="Employees appear here with their PTO and sick balances once they’re added."
        action={
          <Button variant="outlined" size="small" component={Link} to="/employees">
            Go to Employees
          </Button>
        }
      />
    );
  } else if (groups.length === 0) {
    const scope = [searchTerm ? `“${searchTerm}”` : null, selectedDepartmentName ? `in ${selectedDepartmentName}` : null].filter(Boolean).join(' ');
    bodies = singleBody(
      <TableEmptyRow
        colSpan={COLUMN_COUNT}
        title="No employees match these filters"
        body={scope ? `Nothing matches ${scope}.` : undefined}
        action={
          <Button variant="outlined" size="small" onClick={clearFilters}>
            Clear filters
          </Button>
        }
      />
    );
  } else {
    bodies = groups.map(([deptId, deptEmployees]) => (
      <TableBody key={deptId}>
        <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
          <TableCell
            component="th"
            scope="rowgroup"
            colSpan={COLUMN_COUNT}
            sx={{ bgcolor: sbg.bg.surfaceSubtle, py: 1, borderBottom: `1px solid ${sbg.border.subtle}` }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ ...typeStyle('label-sm'), color: sbg.text.primary }}>
                {departmentName(deptId, deptEmployees)}
              </Box>
              <CountBadge count={deptEmployees.length} />
            </Box>
          </TableCell>
        </TableRow>
        {deptEmployees.map((employee) => (
          <TableRow key={employee._id} hover>
            <TableCell sx={{ minWidth: 220 }}>
              <PersonCell
                avatarName={employee.name}
                name={
                  <Box
                    component={Link}
                    to={`/employees/${employee._id}`}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      borderRadius: sbg.radius.xs,
                      '&:hover': { color: sbg.text.link, textDecoration: 'underline', textUnderlineOffset: '2px' },
                      '&:focus-visible': focusRing,
                    }}
                  >
                    {employee.name}
                  </Box>
                }
                secondary={employee.role || undefined}
              />
            </TableCell>
            <TableCell>
              <BalanceCell label="PTO" used={employee.ptoUsed} allotment={employee.ptoAllotment} name={employee.name} />
            </TableCell>
            <TableCell>
              <BalanceCell label="sick time" used={employee.sickTimeUsed} allotment={employee.sickTimeAllotment} name={employee.name} />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="body2" component="span" sx={{ color: sbg.text.primary }}>
                {employee.wfhEligible ? 'Hybrid' : 'In office'}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                {QUICK_LOGS.map(({ type, label, Icon }) => {
                  const actionLabel = `Log ${label} today for ${employee.name}`;
                  const pending = pendingQuickLog === `${employee._id}:${type}`;
                  return (
                    <Tooltip key={type} title={actionLabel}>
                      <span>
                        <IconButton size="small" aria-label={actionLabel} aria-busy={pending || undefined} disabled={pending} onClick={() => onQuickLog(employee, type)}>
                          <Icon size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })}
                <ActionDivider />
                <Tooltip title={`Schedule time off for ${employee.name}`}>
                  <IconButton size="small" aria-label={`Schedule time off for ${employee.name}`} onClick={() => onSchedule(employee)}>
                    <CalendarPlus size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    ));
  }

  return (
    <TableCard
      aria-label="Employees and balances"
      toolbar={toolbar}
      footer={
        !loading && !error && filteredCount > rowsPerPage ? (
          <TablePagination
            component="div"
            count={filteredCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            onPageChange={(_, next) => onPageChange(next)}
          />
        ) : undefined
      }
    >
      <Table aria-label="Employees, balances and time-off actions">
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>PTO balance</TableCell>
            <TableCell>Sick balance</TableCell>
            <TableCell>Work setup</TableCell>
            <TableCell align="right">Log time off</TableCell>
          </TableRow>
        </TableHead>
        {bodies}
      </Table>
    </TableCard>
  );
};

export default LogTimeOffPanel;
